import { SolApp, SolNavbar, SolNavbarItem } from '@sol/components';
import { SolIcon } from '@sol/icons';
import { PlaygroundRouteId } from './models/playground-route-id';
import { LAB_STATUS } from './_generated/lab-status.generated';

function labItem(id: string, displayName: string): SolNavbarItem {
  const status = LAB_STATUS[id];
  return SolNavbarItem.of(id, {
    displayName,
    isNew: status === 'new',
    showBadge: status === 'modified',
  });
}

function rollupBadge(children: SolNavbarItem[]): { isNew: boolean; showBadge: boolean } {
  const isNew = children.some(c => c.isNew);
  const showBadge = !isNew && children.some(c => c.showBadge);
  return { isNew, showBadge };
}

const componentChildren: SolNavbarItem[] = [
  labItem(PlaygroundRouteId.accordion, 'Accordion'),
  labItem(PlaygroundRouteId.action_bar, 'Action bar'),
  labItem(PlaygroundRouteId.alert, 'Alert'),
  labItem(PlaygroundRouteId.avatar, 'Avatar'),
  labItem(PlaygroundRouteId.badge, 'Badge'),
  labItem(PlaygroundRouteId.banner, 'Banner'),
  labItem(PlaygroundRouteId.big_checkbox, 'Big checkbox'),
  labItem(PlaygroundRouteId.big_radiobutton, 'Big radiobutton'),
  labItem(PlaygroundRouteId.breadcrumbs, 'Breadcrumbs'),
  labItem(PlaygroundRouteId.budget, 'Budget'),
  labItem(PlaygroundRouteId.button, 'Button'),
  labItem(PlaygroundRouteId.button_group, 'Button group'),
  labItem(PlaygroundRouteId.calendar, 'Calendar'),
  labItem(PlaygroundRouteId.card, 'Card'),
  labItem(PlaygroundRouteId.chart, 'Chart'),
  labItem(PlaygroundRouteId.chat_message, 'Chat message'),
  labItem(PlaygroundRouteId.chat_thread, 'Chat thread'),
  labItem(PlaygroundRouteId.checkbox, 'Checkbox'),
  labItem(PlaygroundRouteId.code_field, 'Code field'),
  labItem(PlaygroundRouteId.collapsible, 'Collapsible'),
  labItem(PlaygroundRouteId.color_picker, 'Color picker'),
  labItem(PlaygroundRouteId.color_picker_dialog, 'Color picker dialog'),
  labItem(PlaygroundRouteId.confirm_dialog, 'Confirm dialog'),
  labItem(PlaygroundRouteId.context_dialog, 'Context dialog'),
  labItem(PlaygroundRouteId.dashboard_card, 'Dashboard card'),
  labItem(PlaygroundRouteId.date_field, 'Date field'),
  labItem(PlaygroundRouteId.date_field_content, 'Date field content'),
  labItem(PlaygroundRouteId.date_span_field, 'Date span field'),
  labItem(PlaygroundRouteId.dialogs, 'Dialogs'),
  labItem(PlaygroundRouteId.divider, 'Divider'),
  labItem(PlaygroundRouteId.empty_state, 'Empty state'),
  labItem(PlaygroundRouteId.file_upload, 'File upload'),
  labItem(PlaygroundRouteId.form_field, 'Form field'),
  labItem(PlaygroundRouteId.fullscreen_dialog, 'Fullscreen dialog'),
  labItem(PlaygroundRouteId.hierarchical_select, 'Hierarchical select'),
  labItem(PlaygroundRouteId.icon, 'Icon'),
  labItem(PlaygroundRouteId.image, 'Image'),
  labItem(PlaygroundRouteId.inline_loading, 'Inline loading'),
  labItem(PlaygroundRouteId.info_dialog, 'Info dialog'),
  labItem(PlaygroundRouteId.layout, 'Layout'),
  labItem(PlaygroundRouteId.list_view, 'List view'),
  labItem(PlaygroundRouteId.loader, 'Loader'),
  labItem(PlaygroundRouteId.metric, 'Metric'),
  labItem(PlaygroundRouteId.log, 'Log'),
  labItem(PlaygroundRouteId.log_step, 'Log step'),
  labItem(PlaygroundRouteId.navbar, 'Navbar'),
  labItem(PlaygroundRouteId.navbar_item, 'Navbar item'),
  labItem(PlaygroundRouteId.new_table, 'New table'),
  labItem(PlaygroundRouteId.notification, 'Notification'),
  labItem(PlaygroundRouteId.number_field, 'Number field'),
  labItem(PlaygroundRouteId.option, 'Option'),
  labItem(PlaygroundRouteId.paginator, 'Paginator'),
  labItem(PlaygroundRouteId.popover_menu_content, 'Popover menu content'),
  labItem(PlaygroundRouteId.popover_menu_item, 'Popover menu item'),
  labItem(PlaygroundRouteId.popover_menu_header, 'Popover menu header'),
  labItem(PlaygroundRouteId.popover_menu_label, 'Popover menu label'),
  labItem(PlaygroundRouteId.popovers, 'Popovers'),
  labItem(PlaygroundRouteId.progress_bar, 'Progress bar'),
  labItem(PlaygroundRouteId.qr_code, 'QR code'),
  labItem(PlaygroundRouteId.query_element, 'Query element'),
  labItem(PlaygroundRouteId.query_field, 'Query field'),
  labItem(PlaygroundRouteId.query_field_quick_filter, 'Query field quick filter'),
  labItem(PlaygroundRouteId.range_slider, 'Range slider'),
  labItem(PlaygroundRouteId.radiobutton, 'Radiobutton'),
  labItem(PlaygroundRouteId.scroll_view, 'Scroll view'),
  labItem(PlaygroundRouteId.search_field, 'Search field'),
  labItem(PlaygroundRouteId.secret_display, 'Secret display'),
  labItem(PlaygroundRouteId.select, 'Select'),
  labItem(PlaygroundRouteId.select_content, 'Select content'),
  labItem(PlaygroundRouteId.severity_tag, 'Severity tag'),
  labItem(PlaygroundRouteId.severity_box, 'Severity box'),
  labItem(PlaygroundRouteId.slider, 'Slider'),
  labItem(PlaygroundRouteId.shortcut, 'Shortcut'),
  labItem(PlaygroundRouteId.smart_select, 'Smart select'),
  labItem(PlaygroundRouteId.smart_select_content, 'Smart select content'),
  labItem(PlaygroundRouteId.status_tag, 'Status tag'),
  labItem(PlaygroundRouteId.snippet, 'Snippet'),
  labItem(PlaygroundRouteId.spinner, 'Spinner'),
  labItem(PlaygroundRouteId.sso_button, 'SSO button'),
  labItem(PlaygroundRouteId.steps, 'Steps'),
  labItem(PlaygroundRouteId.switch, 'Switch'),
  labItem(PlaygroundRouteId.table, 'Table'),
  labItem(PlaygroundRouteId.tabs, 'Tabs'),
  labItem(PlaygroundRouteId.tag, 'Tag'),
  labItem(PlaygroundRouteId.tag_list, 'Tag list'),
  labItem(PlaygroundRouteId.tag_field_content, 'Tag field content'),
  labItem(PlaygroundRouteId.tag_field_create_dialog, 'Tag field create dialog'),
  labItem(PlaygroundRouteId.tag_field, 'Tag field'),
  labItem(PlaygroundRouteId.text_area, 'Text area'),
  labItem(PlaygroundRouteId.text_box, 'Text box'),
  labItem(PlaygroundRouteId.text_field, 'Text field'),
  labItem(PlaygroundRouteId.time_field, 'Time field'),
  labItem(PlaygroundRouteId.toggle_chip, 'Toggle chip'),
  labItem(PlaygroundRouteId.toggle_chip_group, 'Toggle chip group'),
  labItem(PlaygroundRouteId.toggle_button, 'Toggle button'),
  labItem(PlaygroundRouteId.toolbar, 'Toolbar'),
  labItem(PlaygroundRouteId.tooltip, 'Tooltip'),
  labItem(PlaygroundRouteId.tooltips, 'Tooltips'),
  labItem(PlaygroundRouteId.trend_tag, 'Trend tag'),
  labItem(PlaygroundRouteId.transfer_list, 'Transfer list'),
  labItem(PlaygroundRouteId.tree_view, 'Tree view'),
  labItem(PlaygroundRouteId.validation_messages, 'Validation messages'),
  labItem(PlaygroundRouteId.view_box, 'View box'),
];

const patternChildren: SolNavbarItem[] = [
  labItem(PlaygroundRouteId.details_panel, 'Details panel'),
  labItem(PlaygroundRouteId.dynamic_fields, 'Dynamic fields'),
  labItem(PlaygroundRouteId.error_page, 'Error page'),
  labItem(PlaygroundRouteId.forbidden, 'Forbidden'),
  labItem(PlaygroundRouteId.labeled_row, 'Labeled row'),
  labItem(PlaygroundRouteId.null_controller, 'Null controller'),
  labItem(PlaygroundRouteId.prototype_bar, 'Prototype bar'),
  labItem(PlaygroundRouteId.radio_reveal, 'Radio reveal'),
  labItem(PlaygroundRouteId.variant, 'Variant'),
  labItem(PlaygroundRouteId.workbench, 'Workbench'),
];

const componentsRollup = rollupBadge(componentChildren);
const patternsRollup = rollupBadge(patternChildren);

const playgroundNavbar: SolNavbar = {
  menu: [
    SolNavbarItem.of(PlaygroundRouteId.home, {
      displayName: 'Home',
      icon: SolIcon.overview,
    }),
    SolNavbarItem.of(PlaygroundRouteId.colors, {
      displayName: 'Colors',
      icon: SolIcon.circle_filled_small,
    }),
    SolNavbarItem.of(PlaygroundRouteId.moodboard, {
      displayName: 'Moodboard',
      icon: SolIcon.image,
    }),
    SolNavbarItem.of(PlaygroundRouteId.components_group, {
      displayName: 'Components',
      icon: SolIcon.stack_large,
      children: componentChildren,
      ...componentsRollup,
    }),
    SolNavbarItem.of(PlaygroundRouteId.patterns_group, {
      displayName: 'Patterns',
      icon: SolIcon.grid_dots,
      children: patternChildren,
      ...patternsRollup,
    }),
  ],
  system: [],
  account: [
    SolNavbarItem.of(PlaygroundRouteId.account_themes, {
      displayName: 'Theme',
      children: [
        SolNavbarItem.of(PlaygroundRouteId.mode_light, {
          displayName: 'Light',
          icon: SolIcon.light_mode,
        }),
        SolNavbarItem.of(PlaygroundRouteId.mode_dark, {
          displayName: 'Dark',
          icon: SolIcon.dark_mode,
        }),
        SolNavbarItem.of(PlaygroundRouteId.mode_night, {
          displayName: 'Night',
          icon: SolIcon.night_mode,
        }),
      ],
    }),
    SolNavbarItem.of(PlaygroundRouteId.account_session, {
      children: [
        SolNavbarItem.of(PlaygroundRouteId.account_logout, {
          displayName: 'Logout',
        }),
      ],
    }),
  ],
};

export const apps: SolApp[] = [
  SolApp.ofInternal(
    PlaygroundRouteId.app_playground,
    'Playground',
    SolIcon.application,
    'Playground',
    playgroundNavbar,
    [],
  ),
];
